import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { logError } from "@/lib/log";
import { Copy, Link2, Trash2, X } from "lucide-react";
import { formatDate } from "@/lib/format";

const shareUrl = (token) => `${window.location.origin}/shared/${token}`;

/** Share-link state + actions: load, create (+auto-copy), copy, revoke. */
function useShareLinks() {
  const [links, setLinks] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/share/statements");
      setLinks(data);
    } catch (e) {
      logError("Share links load failed", e);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const copy = async (token) => {
    try {
      await navigator.clipboard.writeText(shareUrl(token));
      toast.success("Link copied — share it with your CA");
    } catch {
      toast.info(shareUrl(token));
    }
  };

  const create = async (label, days, pin) => {
    setBusy(true);
    try {
      const { data } = await api.post("/share/statements", {
        label, expires_days: Number(days), pin: pin || null,
      });
      setLinks((prev) => [data, ...prev]);
      await copy(data.token);
      return true;
    } catch (e) {
      logError("Share link create failed", e);
      toast.error("Could not create the link");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id) => {
    try {
      await api.delete(`/share/statements/${id}`);
      setLinks((prev) => prev.filter((l) => l.id !== id));
      toast.success("Link revoked");
    } catch (e) {
      logError("Share link revoke failed", e);
      toast.error("Could not revoke the link");
    }
  };

  return { links, busy, create, copy, revoke };
}

function LinksList({ links, onCopy, onRevoke }) {
  if (links.length === 0) {
    return <p className="text-sm text-[#6C7565] italic mt-3">No active links yet.</p>;
  }
  return (
    <div className="divide-y divide-[#EAE7DD] mt-2">
      {links.map((l) => (
        <div key={l.id} className="py-3 flex items-center justify-between gap-3" data-testid={`share-link-${l.id}`}>
          <div className="min-w-0">
            <p className="text-sm truncate">{l.label || "Statements link"}</p>
            <p className="font-mono text-[10px] text-[#6C7565] mt-0.5">
              expires {formatDate(l.expires_at)} · <span data-testid={`share-views-${l.id}`}>{l.views || 0} view{(l.views || 0) === 1 ? "" : "s"}</span>
              {l.pin && <span className="text-[#A67C00]" data-testid={`share-pin-${l.id}`}> · PIN {l.pin}</span>}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={() => onCopy(l.token)} data-testid={`share-copy-${l.id}`} title="Copy link" className="border border-[#1A1F16] p-2 hover:bg-[#1A1F16] hover:text-[#F7F5F0]"><Copy className="w-3.5 h-3.5" /></button>
            <button onClick={() => onRevoke(l.id)} data-testid={`share-revoke-${l.id}`} title="Revoke" className="border border-[#D94F3D] text-[#D94F3D] p-2 hover:bg-[#D94F3D] hover:text-white"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        </div>
      ))}
    </div>
  );
}

/** Owner-facing modal: create, copy and revoke read-only "Share with my CA" links. */
export function ShareModal({ onClose }) {
  const { links, busy, create, copy, revoke } = useShareLinks();
  const [label, setLabel] = useState("");
  const [days, setDays] = useState(30);
  const [pin, setPin] = useState("");

  const handleCreate = async () => {
    if (pin && !/^\d{4,6}$/.test(pin)) {
      toast.error("PIN must be 4–6 digits");
      return;
    }
    if (await create(label, days, pin)) {
      setLabel("");
      setPin("");
    }
  };

  return (
    <div className="fixed inset-0 bg-[#1A1F16]/70 z-[70] flex items-center justify-center p-6" onClick={onClose} data-testid="share-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-[#F7F5F0] border border-[#1A1F16] p-8 w-full max-w-lg max-h-[85vh] overflow-y-auto">
        <div className="flex items-start justify-between">
          <div>
            <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#6C7565]">Read-only access</span>
            <h3 className="font-serif text-3xl tracking-tight mt-1">Share with my CA.</h3>
          </div>
          <button onClick={onClose} data-testid="share-modal-close" className="text-[#6C7565] hover:text-[#1A1F16]"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-[#6C7565] mt-3">
          Anyone with the link can view your P&amp;L, Balance Sheet, Cash Statement and Trial Balance — nothing else, and they can't edit anything. Add a PIN for an extra gate, and revoke access anytime.
        </p>

        <div className="mt-6 flex flex-wrap items-end gap-3">
          <label className="flex-1 min-w-[160px]">
            <span className="font-mono text-[10px] tracking-widest uppercase text-[#6C7565] block mb-1">Label (optional)</span>
            <input
              data-testid="share-label-input"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. CA Mehta — FY 25-26"
              className="w-full bg-transparent border-b border-[#1A1F16] py-2 text-sm focus:outline-none"
            />
          </label>
          <label>
            <span className="font-mono text-[10px] tracking-widest uppercase text-[#6C7565] block mb-1">Expires in</span>
            <select data-testid="share-expiry-select" value={days} onChange={(e) => setDays(e.target.value)} className="bg-transparent border-b border-[#1A1F16] py-2 text-sm focus:outline-none">
              <option value={7}>7 days</option>
              <option value={30}>30 days</option>
              <option value={90}>90 days</option>
            </select>
          </label>
          <label className="w-[110px]">
            <span className="font-mono text-[10px] tracking-widest uppercase text-[#6C7565] block mb-1">PIN (optional)</span>
            <input
              data-testid="share-pin-input"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode="numeric"
              placeholder="e.g. 4321"
              className="w-full bg-transparent border-b border-[#1A1F16] py-2 text-sm focus:outline-none num"
            />
          </label>
          <button
            data-testid="share-create-btn"
            onClick={handleCreate}
            disabled={busy}
            className="bg-[#1A1F16] text-[#F7F5F0] px-5 py-2.5 text-xs uppercase tracking-[0.2em] hover:bg-[#2C3628] disabled:opacity-50 flex items-center gap-2"
          >
            <Link2 className="w-3.5 h-3.5" /> {busy ? "Creating…" : "Create link"}
          </button>
        </div>

        <div className="mt-8 border-t border-[#D8D4C7] pt-4" data-testid="share-links-list">
          <span className="font-mono text-[10px] tracking-widest uppercase text-[#6C7565]">Active links</span>
          <LinksList links={links} onCopy={copy} onRevoke={revoke} />
        </div>
      </div>
    </div>
  );
}
